import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { SignalGroupMemberTypes } from "applications/signal/constants";
import { useAddSignalGroupMember } from "applications/signal/hooks/gqlHooks";
import { AlertSnackbarProps } from "features/components/alertSnackbar";
import { useAuth } from "hooks/useAuth";
import { setGlobalMessage } from 'hooks/useGlobalMessage';
import { GroupMemberAddModalSchema, GroupMemberAddModalData } from "./schema";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  setSnackBarMessage: (message: AlertSnackbarProps | null) => void;
}

export default function GroupMemberAddModal({ open, setOpen, setSnackBarMessage }: Props) {
  const { signalUser } = useAuth();
  const { addSignalGroupMember } = useAddSignalGroupMember();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<GroupMemberAddModalData>({
    resolver: zodResolver(GroupMemberAddModalSchema),
    defaultValues: {
      email: "",
      memberType: SignalGroupMemberTypes.editor.ordinal,
    },
  });

  const handleClickClose = () => {
    setOpen(false);
  }

  const onSubmit = async (data: GroupMemberAddModalData) =>  {
    if (!signalUser?.signal_group_id) {
      return;
    }
    const { data: result, error } = await addSignalGroupMember({data: {
      signal_group_id: signalUser.signal_group_id,
      email: data.email,
      member_type: data.memberType,
    }});
    if (error) {
      setGlobalMessage({
        message: "メンバーの追加に失敗しました。",
        severity: "error",
      });
    } else {
      setSnackBarMessage({
        message: "メンバーを追加しました。", // TODO: 招待した場合はメッセージを可変にする？
        severity: "success",
      });
      handleClickClose();
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      onClose={handleClickClose}
    >
      <DialogTitle align="center">
        メンバー追加
      </DialogTitle>
      <IconButton
        onClick={handleClickClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
          <FormControl
            sx={{ width: "450px" }}
            margin="normal"
            error={!!errors.email}
          >
            <TextField
              {...register("email")}
              variant="standard"
              label="メールアドレス"
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          </FormControl>
          <FormControl
            sx={{ width: "120px", marginLeft: "30px" }}
            margin="normal"
            variant="standard"
            error={!!errors.memberType}
          >
            <Controller
              name="memberType"
              control={control}
              render={({ field }) => (
                <>
                  <InputLabel id="member-type-select-label">メンバー種別</InputLabel>
                  <Select
                    {...field}
                    labelId="member-type-select-label"
                  >
                    {Object.values(SignalGroupMemberTypes).map((type) => (
                      <MenuItem key={type.ordinal} value={type.ordinal}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            />
            <FormHelperText>
              {errors.memberType?.message}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
          >
            追加
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
